import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  MdAdd,
  MdMedicalServices,
  MdGridView,
  MdViewList,
  MdLocalHospital,
} from "react-icons/md";

// Composants réutilisables
// Services et hooks
import { OffresService } from "service/offres.service";
import DoctorService from "service/doctor.service";
import { usePageManager } from "hooks/usePageManager";
import { usePhotoUrl } from "hooks/usePhotoUrl";

// Composants spécifiques
import ServiceCard from "./components/service.card";
import ServiceDetails from "./components/service.details";
import FormField from "components/common/formfield";
import PageLayout from "layouts/pagelayout";
import PageHeader from "components/common/pageheader";
import SearchFilters from "components/common/searchfilters";
import EmptyState from "components/common/emptystate";
import Pagination from "components/common/paginnation";
import FormModal from "components/common/formmodal";

// Utilitaires
const getStatusService = (service) => {
  if (service.chefService && service.major && service.members?.length > 0) {
    return "Complet";
  } else if (service.chefService || service.major) {
    return "Partiel";
  } else {
    return "En attente";
  }
};

const filterOptions = [
  { value: "all", label: "Tous", activeClass: "bg-[#2ca01c]" },
  { value: "complet", label: "Complet", activeClass: "bg-green-500" },
  { value: "partiel", label: "Partiel", activeClass: "bg-yellow-500" },
  { value: "attente", label: "En attente", activeClass: "bg-red-500" },
];

const ServicesPage = () => {
  // États de base
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [doctorsLoading, setDoctorsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detailView, setDetailView] = useState(false);
  const [serviceToView, setServiceToView] = useState(null);

  // État du formulaire
  const [formData, setFormData] = useState({
    nom: "",
    description: "",
    pathologie: "",
    chefServiceId: "",
    majorId: "",
    memberIds: [], // Changé en array pour react-select
    photo: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);

  // Hook de gestion de page
  const {
    currentPage,
    totalPages,
    searchTerm,
    filterStatus,
    showFilters,
    viewMode,
    currentItems: currentServices,
    setSearchTerm,
    setFilterStatus,
    goToFirstPage,
    goToPreviousPage,
    goToNextPage,
    goToLastPage,
    resetFilters,
    toggleViewMode,
    toggleFilters,
  } = usePageManager(filteredServices, 3);

  // Hook pour les URLs de photos
  const { getPhotoUrl } = usePhotoUrl();

  // Effects
  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    filterServices();
  }, [services, searchTerm, filterStatus]);

  // Fonctions de chargement des données
  const loadInitialData = async () => {
    await Promise.all([loadServices(), loadDoctors()]);
  };

  const loadServices = async () => {
    setIsLoading(true);
    try {
      const data = await OffresService.getAllServices();
      setServices(data);
      setFilteredServices(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des services:", error);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de récupérer les services. Veuillez réessayer plus tard.",
        confirmButtonColor: "#2ca01c",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadDoctors = async () => {
    try {
      setDoctorsLoading(true);
      const data = await DoctorService.getAllDoctors();
      setDoctors(data);
    } catch (err) {
      console.error("Erreur lors du chargement des docteurs:", err);
      setDoctors([
        {
          id: 1,
          titre: "Pr",
          name: "John Doe",
          specialization: "Cardiologist",
        },
      ]);
    } finally {
      setDoctorsLoading(false);
    }
  };

  // Fonction de filtrage
  const filterServices = () => {
    let results = [...services];

    if (filterStatus !== "all") {
      results = results.filter((service) => {
        const status = getStatusService(service);
        if (filterStatus === "complet") return status === "Complet";
        if (filterStatus === "partiel") return status === "Partiel";
        if (filterStatus === "attente") return status === "En attente";
        return true;
      });
    }

    if (searchTerm.trim() !== "") {
      const searchTermLower = searchTerm.toLowerCase().trim();
      results = results.filter(
        (service) =>
          service.nom?.toLowerCase().includes(searchTermLower) ||
          service.description?.toLowerCase().includes(searchTermLower) ||
          service.pathologie?.toLowerCase().includes(searchTermLower)
      );
    }

    setFilteredServices(results);
  };

  // Gestionnaires de formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* Gestionnaire pour react-select (membres)
  const handleMembersChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      memberIds: selectedOptions || [],
    }));
  };
  */
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, photo: file }));
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    }
  };

  const handlePhotoUploadClick = () => {
    document.getElementById("photo").click();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      document.getElementById("photo").click();
    }
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setFormData((prev) => ({ ...prev, photo: null }));
    document.getElementById("photo").value = "";
  };

  const resetForm = () => {
    setFormData({
      nom: "",
      description: "",
      pathologie: "",
      chefServiceId: "",
      majorId: "",
      memberIds: [],
      photo: null,
    });
    setPhotoPreview(null);
    setSelectedService(null);
  };

  const openForm = (service = null) => {
    if (service) {
      // Convertir les membres en format react-select
      const selectedMembers =
        service.members?.map((member) => ({
          value: member.id,
          label: `${member.titre} ${member.name}`,
        })) || [];

      setFormData({
        nom: service.nom,
        description: service.description,
        pathologie: service.pathologie,
        chefServiceId: service.chefService?.id || "",
        majorId: service.major?.id || "",
        memberIds: selectedMembers,
        photo: null,
      });
      setSelectedService(service);
    } else {
      resetForm();
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.nom || !formData.pathologie) {
      Swal.fire({
        icon: "warning",
        title: "Champs requis",
        text: "Veuillez remplir tous les champs obligatoires.",
        confirmButtonColor: "#2ca01c",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Préparer les données pour l'API
      const apiData = {
        ...formData,
        memberIds: formData.memberIds.map((member) => member.value).join(","),
      };

      if (selectedService) {
        await OffresService.updateService(selectedService.id, apiData);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Le service a été mis à jour avec succès!",
          confirmButtonColor: "#2ca01c",
        });
      } else {
        await OffresService.createService(apiData);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Le service a été créé avec succès!",
          confirmButtonColor: "#2ca01c",
        });
      }

      loadServices();
      closeForm();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du service:", error);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Une erreur est survenue lors de l'enregistrement du service.",
        confirmButtonColor: "#2ca01c",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Êtes-vous sûr?",
      text: "Cette action est irréversible!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2ca01c",
      cancelButtonColor: "#e21b1b",
      confirmButtonText: "Oui, supprimer!",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await OffresService.deleteService(id);
          setServices(services.filter((service) => service.id !== id));
          setFilteredServices(
            filteredServices.filter((service) => service.id !== id)
          );
          Swal.fire({
            title: "Supprimé!",
            text: "Le service a été supprimé avec succès.",
            icon: "success",
            confirmButtonColor: "#2ca01c",
          });
        } catch (error) {
          console.error("Erreur lors de la suppression du service:", error);
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Une erreur est survenue lors de la suppression du service.",
            confirmButtonColor: "#2ca01c",
          });
        }
      }
    });
  };

  // Gestionnaires de vue détaillée
  const viewServiceDetails = (service) => {
    setServiceToView(service);
    setDetailView(true);
  };

  const closeServiceDetails = () => {
    setServiceToView(null);
    setDetailView(false);
  };

  // Préparation des options pour les selects
  const doctorOptions = [
    { value: "", label: "Sélectionnez un médecin" },
    ...doctors.map((doctor) => ({
      value: doctor.id,
      label: `${doctor.titre} ${doctor.name}`,
    })),
  ];

  // Options pour react-select (membres) - sans option vide
  const membersOptions = doctors.map((doctor) => ({
    value: doctor.id,
    label: `${doctor.titre} ${doctor.name} - ${doctor.specialization}`,
  }));

  // Actions du header
  const headerActions = (
    <button
      className="group flex items-center rounded-full bg-[#2ca01c] px-6 py-3 text-white shadow-sm transition-all duration-300 hover:bg-[#238615] hover:shadow-lg"
      onClick={() => openForm()}
    >
      <MdAdd className="mr-2 rounded-full bg-white text-xl text-[#2ca01c] group-hover:bg-[#f0f0f0]" />
      Ajouter un service
    </button>
  );

  // Contenu du formulaire
  const formFields = (
    <>
      <FormField
        label="Nom du service"
        name="nom"
        value={formData.nom}
        onChange={handleInputChange}
        placeholder="Nom du service médical"
        required
      />

      <FormField
        type="textarea"
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleInputChange}
        placeholder="Description détaillée du service"
        rows={4}
      />

      <FormField
        label="Pathologie"
        name="pathologie"
        value={formData.pathologie}
        onChange={handleInputChange}
        placeholder="Pathologie traitée"
        required
      />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField
          type="select"
          label="Chef de service"
          name="chefServiceId"
          value={formData.chefServiceId}
          onChange={handleInputChange}
          options={doctorOptions}
        />

        <FormField
          type="select"
          label="Major"
          name="majorId"
          value={formData.majorId}
          onChange={handleInputChange}
          options={doctorOptions}
        />
      </div>

      {/*<FormField
        type="react-select"
        label="Membres de l'équipe"
        name="memberIds"
        value={formData.memberIds}
        onSelectChange={handleMembersChange}
        options={membersOptions}
        isMulti={true}
        placeholder="Sélectionnez les membres de l'équipe..."
      />
      */}

      <FormField
        type="file"
        label="Photo"
        name="photo"
        onPhotoChange={handlePhotoChange}
        onPhotoUpload={handlePhotoUploadClick}
        onKeyDown={handleKeyDown}
        photoPreview={photoPreview}
        onRemovePhoto={handleRemovePhoto}
      />
    </>
  );

  // Rendu principal
  return (
    <PageLayout>
      {detailView && serviceToView ? (
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <ServiceDetails
            service={serviceToView}
            onClose={closeServiceDetails}
            getPhotoUrl={getPhotoUrl}
          />
        </div>
      ) : (
        <>
          <PageHeader
            title="Services Médicaux"
            subtitle="Centre Hospitalier Universitaire Tambohobe"
            icon={MdLocalHospital}
            actions={headerActions}
            viewMode={viewMode}
            onViewModeChange={toggleViewMode}
            ViewModeIcon={viewMode === "grid" ? MdViewList : MdGridView}
            viewModeText={viewMode === "grid" ? "Vue liste" : "Vue grille"}
          />

          <SearchFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showFilters={showFilters}
            onToggleFilters={toggleFilters}
            filterStatus={filterStatus}
            onFilterStatusChange={setFilterStatus}
            onResetFilters={resetFilters}
            filteredItemsCount={filteredServices.length}
            currentPage={currentPage}
            itemsPerPage={3}
            filterOptions={filterOptions}
            placeholder="Rechercher un service..."
            itemType="service"
          />

          {(() => {
            if (isLoading || doctorsLoading) {
              return (
                <div className="flex items-center justify-center py-12">
                  <div className="border-b-transparent h-16 w-16 animate-spin rounded-full border-4 border-[#2ca01c]"></div>
                </div>
              );
            }

            if (filteredServices.length === 0) {
              return (
                <EmptyState
                  icon={MdMedicalServices}
                  title={
                    services.length === 0
                      ? "Aucun service disponible"
                      : "Aucun service trouvé"
                  }
                  description={
                    services.length === 0
                      ? 'Ajoutez votre premier service en cliquant sur le bouton "Ajouter un service"'
                      : "Aucun service ne correspond à vos critères de recherche"
                  }
                  actionText={
                    services.length === 0 ? "Ajouter un service" : undefined
                  }
                  onAction={
                    services.length === 0 ? () => openForm() : undefined
                  }
                  showResetFilters={true}
                  onResetFilters={resetFilters}
                  hasItems={services.length > 0}
                  hasActiveFilters={searchTerm || filterStatus !== "all"}
                />
              );
            }

            return (
              <>
                <div
                  className={
                    viewMode === "grid"
                      ? "grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
                      : "space-y-6"
                  }
                >
                  {currentServices.map((service) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      onEdit={openForm}
                      onDelete={handleDelete}
                      onView={viewServiceDetails}
                      viewMode={viewMode}
                      getPhotoUrl={getPhotoUrl}
                      getStatusService={getStatusService}
                    />
                  ))}
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onFirstPage={goToFirstPage}
                  onPreviousPage={goToPreviousPage}
                  onNextPage={goToNextPage}
                  onLastPage={goToLastPage}
                />
              </>
            );
          })()}
        </>
      )}

      <FormModal
        isOpen={showForm}
        onClose={closeForm}
        title={selectedService ? "Modifier le service" : "Ajouter un service"}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isUpdate={!!selectedService}
      >
        {formFields}
      </FormModal>
    </PageLayout>
  );
};

export default ServicesPage;
